import React, { useState } from 'react';
import { useIntl } from 'react-intl';
import { useSetAtom, useAtomValue } from 'jotai';
import { Tooltip, IconButton, Stack } from '@mui/material';
import DeleteRoundedIcon from '@mui/icons-material/DeleteRounded';
import { openSnackbarAdviseAtom, userBranchesAtom } from '../../../Atoms';
import {
  createNewDate,
  culture,
  options,
  compareDates,
  addZero,
} from '../../../Helpers/dates';
import ConfirmPopup from '../../../Components/Popup/ConfirmPopup';
import EntryComponent from '../../../Components/ContactListWrapper/EntryComponent';
import {
  OUTCOME_COLORS,
  counterChip,
  getChannelColumn,
} from '../Utils/constants';

//labels depend on the channel: for SMS openCount means delivered to the customer
const getColumns = (isSms, enqueued, intl) => [
  getChannelColumn(isSms, enqueued, intl),
  { id: 'branchName', label: 'BRANCH' },
  {
    id: 'scheduledOn',
    label: 'SCHEDULEDON',
    size: 2,
    formatCallback: (value) =>
      value
        ? `${createNewDate(value).toLocaleDateString(culture, options)} - ${addZero(createNewDate(value))}`
        : null,
  },
  {
    id: 'enqueuedCustomers',
    label: 'CUSTOMERS_NUMBER',
    size: 1.3,
    formatCallback: (value) => counterChip(value, OUTCOME_COLORS.CUSTOMERS),
  },
  {
    id: 'sentCount',
    label: 'SENDED',
    size: 1.1,
    formatCallback: (value) => counterChip(value, OUTCOME_COLORS.SENT),
  },
  {
    id: 'openCount',
    label: isSms ? 'DELIVERED' : 'OPENED',
    size: 1.2,
    formatCallback: (value) => counterChip(value, OUTCOME_COLORS.POSITIVE),
  },
  {
    id: 'notOpenCount',
    label: isSms ? 'NOT_DELIVERED' : 'NOT_OPENED',
    size: 1.3,
    formatCallback: (value, data) =>
      counterChip(
        (data?.sentCount || 0) - (data?.openCount || 0),
        OUTCOME_COLORS.NEGATIVE
      ),
  },
  {
    id: 'tagCounters',
    label: 'CALL_TO_ACTION',
    size: 2,
    formatCallback: (value) =>
      value?.length > 0 ? (
        <Stack direction="row" spacing={1}>
          {value.map(({ tagId, tagName, tagUseCount }) => (
            <Tooltip
              key={tagId}
              arrow
              placement="top"
              title={intl.messages[tagName] || intl.messages.UNAVAILABLE}
            >
              <div>{counterChip(tagUseCount, OUTCOME_COLORS.CTA)}</div>
            </Tooltip>
          ))}
        </Stack>
      ) : null,
  },
];

const MassiveMessageEntry = function MassiveMessageEntry({
  handleDeleteMassiveSMS,
  handleGoToMassiveAnalysis,
  campaignId,
  campaignName,
  messageData = null,
  scheduledOn = null,
  isSupervisor = false,
}) {
  const intl = useIntl();
  const setOpenSnackbarAdviseAtom = useSetAtom(openSnackbarAdviseAtom);
  const userBranches = useAtomValue(userBranchesAtom);

  const [confirmDeletion, setConfirmDeletion] = useState(null);
  const { massiveId, status: enqueued, filters } = messageData;
  const parsedFilters = filters ? JSON.parse(filters) : {};
  const branchId = parsedFilters?.Branches?.[0];
  const isSms = parsedFilters?.IsSMS;

  const handleOpenDeletionConfirmation = (event) => {
    event.stopPropagation(); //prevent row onClick function
    if (
      scheduledOn &&
      compareDates(
        createNewDate(scheduledOn), //date1 to compare
        new Date(), //date2 to compare
        'smaller' //compare operator
      )
    ) {
      setOpenSnackbarAdviseAtom({
        message: intl.messages.CANNOT_DELETE_MASSIVE,
        severity: 'warning',
      });
    } else {
      setConfirmDeletion(event.currentTarget);
    }
  };
  const handleCloseDeletionConfirmation = (event) => {
    event?.stopPropagation();
    setConfirmDeletion(null);
  };
  const confirmDeleteSMS = (event) => {
    event?.stopPropagation();
    setConfirmDeletion(null);
    handleDeleteMassiveSMS(massiveId, campaignId);
  };

  return (
    <>
      <EntryComponent
        data={{
          ...messageData,
          id: massiveId,
          scheduledOn,
          branchName: userBranches?.find((x) => x.id === branchId)
            ?.branchDescription,
        }}
        columns={getColumns(isSms, enqueued, intl)}
        hideDivider
        statusSize="auto"
        customClick={() =>
          handleGoToMassiveAnalysis(
            campaignName,
            branchId,
            isSms ? 'SMS' : 'EMAIL'
          )
        }
        customStatusChipCallback={() =>
          isSupervisor && (
            <Tooltip title={intl.messages.DELETE} placement="bottom">
              <IconButton
                id="delete-massive-button"
                onClick={handleOpenDeletionConfirmation}
              >
                <DeleteRoundedIcon />
              </IconButton>
            </Tooltip>
          )
        }
      />
      {/* outside the row so popup clicks don't bubble to the row onClick */}
      {isSupervisor && (
        <ConfirmPopup
          open={Boolean(confirmDeletion)}
          anchorEl={confirmDeletion}
          close={handleCloseDeletionConfirmation}
          handleConfirm={confirmDeleteSMS}
          confirmText={intl.messages.CONFIRM_MESSAGE_DELETION}
        />
      )}
    </>
  );
};

export default MassiveMessageEntry;
